import React, {
  useState,
  useEffect,
  useContext,
  useRef,
  MouseEvent,
  useCallback,
  useMemo,
  forwardRef,
  useImperativeHandle
} from 'react';
import { useQueryParams } from 'hooks/useHookRouterCompat';
import Order, * as Ord from 'data/Order';
import Vendor from 'data/Vendor';
import { isBrandLevelRoles, isVendor, hasOfmpAdminProfile } from 'data/User';
import ConsignmentsTable from 'components/Orders/ConsignmentsTable';
import Page from 'components/UI/Page';
import PageHeading from 'components/UI/PageHeading';
import { renderToast, toast } from 'components/UI/ToastNotification';
import GenericTableWrapper from 'components/UI/GenericTable/GenericTableWrapper';
import { useAPI } from 'lib/API';
import { useAuth } from 'lib/Auth';
import { replace } from 'lib/Array';
import { loadOrders } from 'requests/order';
import { SocketContext } from 'context/socket';
import initialColumns from 'constants/orderColumns';
import { capitalize, currencySign } from 'utils/formatters';
import { COUNTRY_ISO } from 'constants/country';
import { AFFILIATE_STRINGS } from 'constants/affiliate';
import { CommissionActionMap } from 'data/AffiliateMember';
import { FontWeight } from 'lib/styles';
import { Text } from 'styles/themeColors';
import { CONSIGNMENT_TYPES } from 'constants/order';
import FeatureFlag from 'data/FeatureFlag';
import { mapOrderGridColumns, getSteps, mapOrderData } from 'utils/gridColumnMappings';
import useFetchGridData from 'hooks/useFetchGridData';
import {
  handleOnSearchKeyChange,
  handleOnSortChange,
  handleOnRowClick,
  SortByConfig
} from 'utils/grid';
import { shouldShowColumn } from 'utils/order';
import { useCountries } from 'hooks/useCountries';
import { useBusinessUnitBrandFiltering } from 'hooks/useBusinessUnitBrandFiltering';
import { BusinessUnit } from 'data/BusinessUnit';
import { BUSINESS_UNIT_RELATIONS } from 'constants/credit';
import { useCountrySelector, useCommissionStatusSelector, useTrackTableColumnsWidth, CommissionStatus } from './orderlist.hooks';
import { getItems } from 'lib/SessionStorage';
import { STORAGE_KEYS } from 'constants/general';
import { unique } from 'lib/filters';

const NOOP = () => {};

interface Props {
  selectedVendor?: Vendor;
  selectedBusinessUnit?: BusinessUnit;
  page?: string;
  shouldRedirect?: boolean;
  pagePadding?: string;
  headingText?: string;
  navigateBackUrl?: string;
  companyId?: string;
  featureFlags: FeatureFlag;
  consignmentType?: string;
  baseRoute?: string;
  affiliateCustomerId?: number;
  commissionStatus?: string;
  onCommissionStatusChange?: (status: string) => void;
  enableSelection?: boolean;
  selectedRows?: any[];
  onSelectionChange?: (selectedRows: any[]) => void;
  consignmentStatusMap?: Record<string, string>;
  consignmentCommissionMap?: Record<string, number>;
  selectedConsignments?: string[];
  onConsignmentSelect?: (ids: string[]) => void;
  isRowSelectable?: (rowData: any) => boolean;
  paidCommissionStatus?: string;
  consignmentIds?: string;
  affiliateCode?: string | null;
  onDataLoaded?: () => void;
};

const OrdersList = forwardRef<{ refresh: () => void }, Props>(({
  selectedVendor,
  selectedBusinessUnit,
  page,
  shouldRedirect,
  pagePadding,
  headingText = 'Orders',
  navigateBackUrl,
  companyId,
  featureFlags,
  consignmentType,
  baseRoute = 'orders',
  affiliateCustomerId,
  commissionStatus,
  onCommissionStatusChange,
  enableSelection,
  selectedRows,
  onSelectionChange,
  consignmentStatusMap,
  consignmentCommissionMap,
  selectedConsignments,
  onConsignmentSelect,
  isRowSelectable,
  paidCommissionStatus,
  consignmentIds,
  affiliateCode,
  onDataLoaded,
}, ref) => {
  const { user } = useAuth();
  const tableRef = useRef<{ tableContainerDiv?: any }>(null);
  const isVendorUser = isVendor(user!);
  const isAdmin = hasOfmpAdminProfile(user!);
  const [queryParams, setQueryParams] = useQueryParams();
  const [currentPage, setCurrentPage] = useState(page ? parseInt(page) : 1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [searchTerm, setSearchTerm] = useState('');
  const filteredInitialColumns = initialColumns.filter(column =>
    shouldShowColumn({
      columnField: column.field,
      isAdmin,
      isVendorUser,
      consignmentType,
      selectedVendor,
      selectedBusinessUnit
    })
  );
  const [sortBy, setSortBy] = useState<SortByConfig | undefined>(filteredInitialColumns.find(c => c.defaultSort !== undefined));
  const [pagesCount, setPagesCount] = useState<number>();
  const [showOpen, setShowOpen] = useState(false);
  const [showPendingCancelationEmail, setShowPendingCancelationEmail] = useState(false);
  const [ordersResponse, setOrdersResponse] = useState<Ord.OrdersList>({
    page: 1,
    totalPages: 1,
    size: 1,
    totalItems: 1,
    orders: []
  });
  const initialLoadDoneRef = useRef(false);

  useEffect(() => {
    if (!initialLoadDoneRef.current && ordersResponse.orders.length > 0) {
      initialLoadDoneRef.current = true;
      onDataLoaded?.();
    }
  }, [ordersResponse.orders, onDataLoaded]);
  const { columnWidths, updateColumnWidth } = useTrackTableColumnsWidth(tableRef)
  const socket = useContext(SocketContext);


  const { selectedCountry, countrySelector } = useCountrySelector();
  const commissionStatuses: CommissionStatus[] = useMemo(() => getItems(STORAGE_KEYS.COMMISSION_STATUSES) || [], []);
  const commissionActions = useMemo(() => getItems(STORAGE_KEYS.COMMISSION_ACTIONS) as CommissionActionMap | null, []);
  const { commissionStatusSelector } = useCommissionStatusSelector(
    commissionStatus || commissionActions?.approve.fromStatus || '',
    onCommissionStatusChange ?? NOOP,
    commissionStatuses
  );
  const { countries } = useCountries();

  // Use reusable business unit filtering hook
  const { brandIdsStringForAPI } = useBusinessUnitBrandFiltering({
    selectedBusinessUnit,
    selectedVendorId: selectedVendor?.id
  });

  useEffect(() => {
    if (queryParams.ordersPage) {
      setCurrentPage(queryParams.ordersPage);
      setQueryParams({ country: undefined, ordersPage: undefined });
    }
  }, []);

  const onConsignmentsUpdate = useCallback((data: any) => {
    const vendorId = selectedVendor?.id || null;

    orderReq.run(itemsPerPage, 1, 0, vendorId, data.orderId, null, null, null, null, null, null, brandIdsStringForAPI, companyId, null, consignmentType, affiliateCustomerId, commissionStatus, consignmentIds, affiliateCode);
}, [itemsPerPage, selectedVendor?.id, brandIdsStringForAPI, companyId, selectedBusinessUnit, consignmentType, affiliateCustomerId, commissionStatus, consignmentIds, affiliateCode]);

  useEffect(() => {
    socket.on('updateOrder', onConsignmentsUpdate);

    return () => {
      socket.off('updateOrder', onConsignmentsUpdate);
    }
  }, [socket, onConsignmentsUpdate]);

  const onAPIError = useCallback((err: any) => renderToast(toast.TYPE.ERROR, err.message), []);

  const filterConsignmentsByCommissionStatus = useCallback((consignments: any[]) => {
    if (!consignmentStatusMap) return consignments;
    return consignments.filter((c: any) =>
      commissionStatus
        ? consignmentStatusMap[c.consignmentId] === commissionStatus
        : consignmentStatusMap[c.consignmentId]
    );
  }, [consignmentStatusMap, commissionStatus]);

  const onSortChange = useCallback((index: number, direction: string) => handleOnSortChange(index, direction, filteredInitialColumns, setSortBy), [filteredInitialColumns]);

  const onSearchTermSet = useCallback((value: string) => handleOnSearchKeyChange(value, setSearchTerm, setCurrentPage), []);

  const onRowClick = useCallback((_?: MouseEvent<Element>, row?: any) => {
    const orderBaseRoute = baseRoute || (consignmentType ? 'subscriptions/orders' : 'orders');
    const navigableConsignments = affiliateCustomerId
      ? filterConsignmentsByCommissionStatus(row!.consignments)
      : row!.consignments;

    if (navigableConsignments.length === 0) return;

    const url = (navigableConsignments.length > 1)
      ? `/${orderBaseRoute}/split-order-details/${row!.orderId}`
      : `/${orderBaseRoute}/order-details/${navigableConsignments[0].consignmentId.slice(4)}`;

    const params = { country: row.country?.countryIso, ordersPage: currentPage };
    const isNavigationBlocked = (isVendorUser || isBrandLevelRoles(user!)) && navigableConsignments.length > 1;

    handleOnRowClick(url, params, isNavigationBlocked);
  }, [currentPage, isVendorUser, consignmentType, baseRoute, affiliateCustomerId, filterConsignmentsByCommissionStatus]);

  const onSizeChange = useCallback((newSize: number) => {
    setItemsPerPage(newSize);
    // Don't reset to page 1 to avoid reloading
  }, []);

  const onOpenOrdersSwitchChange = useCallback(() => {
    setShowOpen(!showOpen);
    setCurrentPage(1);
  }, [showOpen]);

  const onCancelOrdersSwitchChange = useCallback(() => {
    setShowPendingCancelationEmail(!showPendingCancelationEmail);
    setCurrentPage(1);
  }, [showPendingCancelationEmail]);

  const ordersFirstPageReq = useAPI({
    deferFn: loadOrders,
    onResolve: (result: Ord.OrdersList) => {
      const mappedOrders = mapOrderData(result.orders, isVendorUser, featureFlags, selectedVendor);
      setPagesCount(result.totalPages)
      setOrdersResponse({ ...result, orders: mappedOrders });
    },
    onReject: onAPIError,
    debugLabel: 'ordersFirstPageReq'
  });

  const ordersRestPagesReq = useAPI({
    deferFn: loadOrders,
    onResolve: (result: Ord.OrdersList) => {
      const mappedOrders = mapOrderData(result.orders, isVendorUser, featureFlags, selectedVendor);
      setPagesCount(result.totalPages)
      setOrdersResponse({ ...result, orders: mappedOrders });
    },
    onReject: onAPIError,
    debugLabel: 'ordersRestPagesReq'
  });

  const orderReq = useAPI({
    deferFn: loadOrders,
    onResolve: (result: Ord.OrdersList) => {
      const mappedOrders = mapOrderData(result.orders, isVendorUser, featureFlags, selectedVendor);
      setPagesCount(result.totalPages)
      setOrdersResponse((prevState: Ord.OrdersList) => {
        if (mappedOrders.length > 0) {
          const foundOrder = prevState.orders.find(order => order.orderId === mappedOrders[0].orderId)

          if (foundOrder) {
            const newOrders = replace(prevState.orders, order => order.orderId === mappedOrders[0].orderId, mappedOrders[0]);

            return {
              ...prevState,
              orders: newOrders
            }
          } else if (!affiliateCustomerId || !commissionStatus) {
            const newOrders = [...prevState.orders];

            newOrders.unshift(mappedOrders[0]);

            return {
              ...prevState,
              orders: newOrders
            }
          }
        }

        return prevState;
      });
    },
    onReject: onAPIError,
    debugLabel: 'orderReq'
  });

  const selectedCountryId = selectedCountry === 'All countries' ? undefined :
    countries.find(c => c.countryName === selectedCountry)?.id;


  /* fetch orders */
  useFetchGridData({
    firstPageRequest: ordersFirstPageReq,
    restPagesRequest: ordersRestPagesReq,
    changePage: setCurrentPage,
    size: itemsPerPage,
    vendorId: selectedVendor && selectedVendor.id,
    currentPage,
    sortBy,
    countryId: selectedCountryId,
    searchTerm,
    showOpen,
    showPendingCancellationEmail: showPendingCancelationEmail,
    brandIds: brandIdsStringForAPI,
    companyId,
    consignmentType,
    affiliateCustomerId,
    commissionStatus,
    consignmentIds,
    affiliateCode,
  });

  useImperativeHandle(ref, () => ({
    refresh: () => {
      ordersFirstPageReq.run(
        itemsPerPage, 1, 0,
        selectedVendor?.id, null, sortBy?.field, sortBy?.defaultSort,
        searchTerm, selectedCountryId, showOpen, showPendingCancelationEmail,
        brandIdsStringForAPI, companyId, null, consignmentType, affiliateCustomerId, commissionStatus, consignmentIds, affiliateCode
      );
    }
  }));

  const isLoading = (ordersFirstPageReq.isLoading || ordersRestPagesReq.isLoading);

  const mappedColumns = mapOrderGridColumns(filteredInitialColumns, isVendorUser, featureFlags, sortBy, selectedVendor).map((column: any) => {
    if (affiliateCustomerId && column.field === 'totalPriceWithTax') {
      return {
        ...column,
        title: AFFILIATE_STRINGS.labels.totalCommission,
        field: 'totalCommission',
        render: (rowData: any) => {
          const countryIso = rowData.country?.countryIso || COUNTRY_ISO.US;
          return currencySign(countryIso, Number(Number(rowData.totalCommission || 0).toFixed(2)));
        }
      };
    }
    return column;
  });

  if (affiliateCustomerId && consignmentStatusMap) {
    const warehouseIdx = mappedColumns.findIndex(c => c.field === 'warehouse');
    if (warehouseIdx !== -1) {
      mappedColumns.splice(warehouseIdx + 1, 0, {
        title: 'Commission Status',
        field: 'commissionStatus',
        headerStyle: { color: Text.primary, fontWeight: FontWeight.bold },
        width: 'auto',
        render: (rowData: any) => {
          const statuses = filterConsignmentsByCommissionStatus(rowData.consignments)
            .map((c: any) => consignmentStatusMap![c.consignmentId]);
          const uniqueStatuses = statuses.filter(unique);
          const display = uniqueStatuses.length === 1 ? uniqueStatuses[0] : (uniqueStatuses.length > 1 ? 'Multiple' : 'N/A');
          return capitalize(display);
        }
      });
    }
  }

  const displayOrders = useMemo(() => {
    if (!affiliateCustomerId || !consignmentStatusMap) return ordersResponse.orders;
    return ordersResponse.orders.map((order: any) => {
      const visibleConsignments = filterConsignmentsByCommissionStatus(order.consignments);
      if (visibleConsignments.length !== 1) return order;
      return {
        ...order,
        state: visibleConsignments[0].state,
        consignments: visibleConsignments,
        totalCommission: consignmentCommissionMap?.[visibleConsignments[0].consignmentId] || 0,
      };
    });
  }, [ordersResponse.orders, affiliateCustomerId, consignmentStatusMap, filterConsignmentsByCommissionStatus, consignmentCommissionMap]);

  useEffect(() => updateColumnWidth(), [currentPage, displayOrders.length, itemsPerPage, updateColumnWidth])

  const preselectedConsignmentIds = useMemo(() =>
    consignmentIds ? consignmentIds.split(',').filter(Boolean) : undefined,
    [consignmentIds]
  );

  // Pre-select order rows matching preselectedConsignmentIds on initial load
  useEffect(() => {
    if (preselectedConsignmentIds && preselectedConsignmentIds.length > 0 && displayOrders.length > 0 && onSelectionChange && !(selectedRows || []).length) {
      const matchingRows = displayOrders.filter((order: any) =>
        (order.consignments || []).some((c: any) => preselectedConsignmentIds.includes(c.consignmentId))
      );
      if (matchingRows.length > 0) {
        onSelectionChange(matchingRows);
      }
    }
  }, [displayOrders, preselectedConsignmentIds]);

  const detailPanel = useMemo(() => [{
    tooltip: 'Show more',
    render: (rowData: Order) => {
      const visibleConsignments = filterConsignmentsByCommissionStatus(rowData.consignments);

      if (visibleConsignments.length <= 1) {
        return null;
      }
      return (
        <ConsignmentsTable
          consignments={visibleConsignments}
          isVendor={isVendorUser}
          getSteps={getSteps}
          navigateBackUrl={navigateBackUrl}
          page={currentPage}
          dowJonesStatus={rowData.dowJones ? rowData.dowJones.status : ''}
          featureFlags={featureFlags}
          columnWidths={columnWidths}
          country={rowData.country?.countryIso}
          consignmentStatusMap={consignmentStatusMap}
          consignmentCommissionMap={consignmentCommissionMap}
          selectedConsignments={selectedConsignments}
          onConsignmentSelect={onConsignmentSelect}
          paidCommissionStatus={paidCommissionStatus}
          preselectedConsignmentIds={preselectedConsignmentIds}
        />
      );
    }
  }], [isVendorUser, currentPage, featureFlags, columnWidths, navigateBackUrl, filterConsignmentsByCommissionStatus, consignmentStatusMap, consignmentCommissionMap, selectedConsignments, onConsignmentSelect, paidCommissionStatus, preselectedConsignmentIds]);

  const switchers = useMemo(() => {
    const base = [{
      label: 'Show open orders only',
      checked: showOpen,
      onChange: onOpenOrdersSwitchChange
    }];

    if (!isVendorUser && consignmentType !== CONSIGNMENT_TYPES.SUBSCRIPTION) {
      base.push({
        label: 'Pending cancelation email only',
        checked: showPendingCancelationEmail,
        onChange: onCancelOrdersSwitchChange
      });
    }

    return base;
  }, [isVendorUser, consignmentType, showOpen, showPendingCancelationEmail, onOpenOrdersSwitchChange, onCancelOrdersSwitchChange]);

  const selectors = useMemo(
    () => [countrySelector],
    [countrySelector]
  );

  const leftSelectors = useMemo(
    () => affiliateCustomerId ? [commissionStatusSelector] : undefined,
    [affiliateCustomerId, commissionStatusSelector]
  );

  if (isAdmin && consignmentType === CONSIGNMENT_TYPES.SUBSCRIPTION && !selectedBusinessUnit) {
    return (
      <Page padding={pagePadding}>
        <span style={{ margin: '0px 0px', fontSize: '24px' }}>{BUSINESS_UNIT_RELATIONS.PLEASE_SELECT_BUSINESS_UNIT}</span>
      </Page>
    );
  }

  return (
    <Page padding={pagePadding}>
      {headingText && <PageHeading>{headingText}</PageHeading>}
      <GenericTableWrapper
        columns={mappedColumns}
        data={displayOrders}
        isLoading={isLoading}
        onRowClick={onRowClick}
        wrapperWidth="100%"
        toolbar
        overrideToolbar
        onOrderChange={onSortChange}
        detailPanel={detailPanel}
        borderBetweenRows={true}
        switchers={switchers}
        customSearch={{
          placeholder: 'Search',
          value: searchTerm,
          onChange: onSearchTermSet
        }}
        selectors={selectors}
        leftSelectors={leftSelectors}
        tableRef={tableRef}
        disableBorderCollapse={false}
        customPagination={{
          itemsPerPage,
          setItemsPerPage: onSizeChange,
          currentPage,
          setCurrentPage,
          totalPages: pagesCount!,
          baseRoute,
          shouldRedirect
        }}
        sortBy={sortBy}
        enableSelection={enableSelection}
        selectedRows={selectedRows}
        onSelectionChange={onSelectionChange}
        isRowSelectable={isRowSelectable}
      />
    </Page>
  );
});

export default React.memo(OrdersList);
